import uuid from 'node-uuid';
import moment from 'moment';
import { isEqual, get, orderBy, findIndex } from 'lodash';
import {
	dateFormat,
	dateFormatToday,
	TYPE_BACS_CREDIT,
	TYPE_BACS_DEBIT,
	TYPE_FASTER_PAYMENTS,
	TYPE_CHAPS,
} from '../config';
import { CONST_FILE_UPLOAD } from '../config/constants';
import { isDateToday, formattedDate } from './dateUtils';

const {
	CLIENT_SIDE_ERROR: { BACS, FPS },
	BATCH_TYPE,
} = CONST_FILE_UPLOAD;
const fileUploadValidAmount = /^(?=.*[1-9])\d{1,8}(?:\.\d{1,2})?$/;
const validSortCode = /^\d{6}$/;
const validAccountNumber = /^\d{8}$/;
const validPayeeNameBACS = /^(?!\s)([a-zA-Z0-9\s/.-]){1,35}$/;
const validPaymentReferenceBACSAndFPS = /^(?!\s)([a-zA-Z0-9\s/.,()='‘+:?!”"%&*<>;@#-]){1,18}$/;

export const getDataIntoObject = (data = [], type) => {
	switch (type) {
		case TYPE_CHAPS:
		case TYPE_FASTER_PAYMENTS: {
			const [from_sort_code, from_account_number, name, reference, sort_code, account_number, amount] = data;
			const rowData = {
				id: uuid.v1(),
				from_sort_code,
				from_account_number,
				name,
				reference,
				sort_code,
				account_number,
				amount,
			};
			return rowData;
		}
		default: {
			const [name, reference, sort_code, account_number, amount, acc_code, txn_code] = data;
			const rowData = {
				id: uuid.v1(),
				name,
				reference,
				sort_code,
				account_number,
				amount,
				acc_code,
				txn_code,
			};
			return rowData;
		}
	}
};
export const getImportedPaymentsList = (results, type) => {
	switch (type) {
		case TYPE_CHAPS:
		case TYPE_FASTER_PAYMENTS:
			return results.map(
				([from_sort_code, from_account_number, name, reference, sort_code, account_number, amount]) => ({
					id: uuid.v4(),
					from: {
						sortCode: from_sort_code,
						accountNumber: from_account_number,
					},
					to: {
						displayName: name,
						accountNumber: account_number,
						sortCode: sort_code,
					},
					amount,
					reference: type === TYPE_CHAPS ? undefined : reference,
					reference_high_value: type === TYPE_CHAPS ? reference : undefined,
				})
			);
		case TYPE_BACS_CREDIT:
			return results.map(([name, reference, sort_code, account_number, amount, , , hmrcReference]) => ({
				id: uuid.v4(),
				to: {
					displayName: name,
					accountNumber: account_number,
					sortCode: sort_code,
				},
				amount,
				reference,
				hmrcReference,
			}));
		case TYPE_BACS_DEBIT:
			return results.map(([name, reference, sort_code, account_number, amount, , transactionCode]) => ({
				id: uuid.v4(),
				from: {
					displayName: name,
					accountNumber: account_number,
					sortCode: sort_code,
				},
				amount,
				reference,
				transactionCode,
			}));
		default:
			return null;
	}
};

export const getCreditRules = (fileData) => {
	const validAccountCode = /^0$/;
	const validTransactionCode = /^99$/;
	const validHMRCReference = /^([A-z0-9-./]{4})?$/;

	const headers = [
		'Payee Name',
		'Payment Reference',
		'Payee Sort Code',
		'Payee Account Number',
		'Amount',
		'Account Code',
		'Transaction Code',
	];

	const rules = [
		{ name: 'payee name', test: (value) => validPayeeNameBACS.test(value) },
		{ name: 'payment reference', test: (value) => value === '' || validPaymentReferenceBACSAndFPS.test(value) },
		{ name: 'sort code', test: (value) => validSortCode.test(value) },
		{ name: 'account number', test: (value) => validAccountNumber.test(value) },
		{ name: 'amount', test: (value) => fileUploadValidAmount.test(value) },
		{ name: 'account code', test: (value) => validAccountCode.test(value) },
		{ name: 'transaction code', test: (value) => validTransactionCode.test(value) },
	];

	if (fileData[0].length > 7) {
		headers.push('HMRC Reference');
		rules.push({ name: 'HMRC reference', test: (value) => validHMRCReference.test(value) });
	}

	const isHeaderRow = (row) => row && isEqual(row.slice(0, headers.length), headers);

	return {
		isHeaderRow,
		rules,
	};
};

export const getDebitRules = () => {
	const validAccountCode = /^0$/;
	const validTransactionCode = /^(01|17|18|19)$/;

	const headers = [
		'Payee Name',
		'Payment Reference',
		'Payee Sort Code',
		'Payee Account Number',
		'Amount',
		'Account Code',
		'Transaction Code',
	];

	const isHeaderRow = (row) => row && isEqual(row.slice(0, headers.length), headers);

	const rules = [
		{ name: 'payee name', test: (value) => validPayeeNameBACS.test(value) },
		{ name: 'payment reference', test: (value) => validPaymentReferenceBACSAndFPS.test(value) },
		{ name: 'sort code', test: (value) => validSortCode.test(value) },
		{ name: 'account number', test: (value) => validAccountNumber.test(value) },
		{ name: 'amount', test: (value) => fileUploadValidAmount.test(value) },
		{ name: 'account code', test: (value) => validAccountCode.test(value) },
		{ name: 'transaction code', test: (value) => validTransactionCode.test(value) },
	];
	return {
		isHeaderRow,
		rules,
	};
};

export const getFPSRules = () => {
	const validPayeeName = /^(?!\s)([a-zA-Z0-9\s/.-]){1,40}$/;

	const headers = [
		'From Sort Code',
		'From Account Number',
		'Payee Name',
		'Payment Reference',
		'Payee Sort Code',
		'Payee Account Number',
		'Amount',
	];

	const isHeaderRow = (row) => row && isEqual(row.slice(0, headers.length), headers);

	const rules = [
		{
			name: 'from sort code',
			test: (value) => {
				if (value !== '') return validSortCode.test(value);
				return true;
			},
		},
		{
			name: 'from account number',
			test: (value) => {
				if (value !== '') return validAccountNumber.test(value);
				return true;
			},
		},
		{ name: 'payee name', test: (value) => validPayeeName.test(value) },
		{ name: 'payment reference', test: (value) => validPaymentReferenceBACSAndFPS.test(value) },
		{ name: 'sort code', test: (value) => validSortCode.test(value) },
		{ name: 'account number', test: (value) => validAccountNumber.test(value) },
		{ name: 'amount', test: (value) => fileUploadValidAmount.test(value) },
	];
	return {
		isHeaderRow,
		rules,
	};
};

export const displayDate = (date) => {
	if (isDateToday(date, moment())) return formattedDate(date, dateFormatToday);
	return formattedDate(date, dateFormat);
};
export const getBacsPaymentsToAdd = (responses, importedPaymentsData, isCreditData) => {
	const remappedResponses = responses.map((response) => {
		const importedPayment = importedPaymentsData.find((val) => response.id === val.id);
		const { to, from, id: originalId, ...rest } = importedPayment;
		const {
			body: { id },
		} = response;
		if (isCreditData) return { ...rest, originalId, id: uuid.v1(), toAccount: id };
		return { ...rest, originalId, id: uuid.v1(), fromAccount: id };
	});

	return orderBy(remappedResponses, (remappedResponse) =>
		findIndex(importedPaymentsData, { id: remappedResponse.originalId })
	);
};

export const getPayeesFromImportedPaymentsList = (importedPaymentsData, isCreditData = true, isSingleUse) =>
	importedPaymentsData.map((data) => {
		const { fromAccount, id, reference, reference_high_value } = data;
		const { displayName, sortCode, accountNumber } = isCreditData ? data.to : data.from;
		return {
			fromAccount,
			id,
			name: displayName,
			sort_code: sortCode,
			account_number: accountNumber,
			display_name: displayName,
			reference,
			reference_high_value,
			single_use: isSingleUse,
		};
	});

export const getFPSorCHAPSPaymentsToAdd = (responses, importedPaymentsData) => {
	const remappedResponses = responses.map((response) => {
		const importedPayment = importedPaymentsData.find((val) => response.id === val.id);
		const { to, from, reference, reference_high_value, id: originalId, ...rest } = importedPayment;
		const {
			body: { id },
		} = response;
		return {
			...rest,
			originalId,
			id: uuid.v1(),
			toAccount: id,
			batch_type: BATCH_TYPE,
			reference: reference || reference_high_value,
		};
	});

	// This ensures the order by what the customer has specified the payments is maintained, as the order
	// from the service response is random.
	return orderBy(remappedResponses, (remappedResponse) =>
		findIndex(importedPaymentsData, { id: remappedResponse.originalId })
	);
};

export const getFormattedFailedPayments = (responses, importedPaymentsData) =>
	responses.map((response) => {
		const importedPayment = importedPaymentsData.find((val) => response.id === val.id);
		const from = get(importedPayment, 'from');
		const to = get(importedPayment, 'to');
		const errorStatus = get(response, 'body.coded_exceptions[0].error_status', '');
		const name = get(to, 'displayName', get(from, 'displayName'));

		return {
			hmrcReference: get(importedPayment, 'hmrcReference'),
			reference: get(importedPayment, 'reference', get(importedPayment, 'reference_high_value')),
			sort_code: get(to, 'sortCode', get(from, 'sortCode')),
			account_number: get(to, 'accountNumber', get(from, 'accountNumber')),
			display_name: name,
			name,
			from_sort_code: get(from, 'sortCode'),
			from_account_number: get(from, 'accountNumber'),
			error_status: errorStatus,
		};
	});

export const getCHAPSRules = () => {
	const validPayeeName = /^(?!\s)([a-zA-Z0-9\s/.-]){1,33}$/;
	const validPaymentReference = /^(?!\s)([a-zA-Z0-9\s/?:().,'‘+-]){1,140}$/;

	const headers = [
		'From Sort Code',
		'From Account Number',
		'Payee Name',
		'Payment Reference',
		'Payee Sort Code',
		'Payee Account Number',
		'Amount',
	];

	const isHeaderRow = (row) => row && isEqual(row.slice(0, headers.length), headers);

	const rules = [
		{
			name: 'from sort code',
			test: (value) => {
				if (value !== '') return validSortCode.test(value);
				return true;
			},
		},
		{
			name: 'from account number',
			test: (value) => {
				if (value !== '') return validAccountNumber.test(value);
				return true;
			},
		},
		{ name: 'payee name', test: (value) => validPayeeName.test(value) },
		{
			name: 'payment reference',
			test: (value) => {
				if (value !== '') return validPaymentReference.test(value);
				return true;
			},
		},
		{ name: 'sort code', test: (value) => validSortCode.test(value) },
		{ name: 'account number', test: (value) => validAccountNumber.test(value) },
		{ name: 'amount', test: (value) => fileUploadValidAmount.test(value) },
	];
	return {
		isHeaderRow,
		rules,
	};
};

export const checkEmptySortCodeOrAccountNumber = (firstRowData) => [firstRowData[0], firstRowData[1]].includes('');

export const getErrorKey = (type) =>
	({
		[TYPE_BACS_CREDIT]: BACS,
		[TYPE_BACS_DEBIT]: BACS,
		[TYPE_FASTER_PAYMENTS]: FPS,
		[TYPE_CHAPS]: FPS,
	}[type]);

export const getFromAccountIds = (importedPaymentList) =>
	importedPaymentList.length ? importedPaymentList.map((account) => account.fromAccount) : [];

export const getFromAccountNumbers = (importedPaymentList) =>
	importedPaymentList.length ? importedPaymentList.map((account) => account.from.accountNumber) : [];

export const getUniqueAccounts = (accounts) => accounts.filter((value, index, array) => array.indexOf(value) === index);
